import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';

import useDebounce from '~hooks/useDebounce';
import useExternalTransfers, { ExternalTransferEntry } from '~hooks/useExternalTransfers';
import { useAsteriskDialler } from '~providers/AsteriskDiallerProvider';
import { SelectOption } from '~providers/AsteriskDiallerProvider/reducer';

const ManualOutboundPanel = () => {
  const { state: dialler, dispatch, socketRef, loadTimezones } = useAsteriskDialler();
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const { list: externalTransferList, getNextPage } = useExternalTransfers({ search: debouncedSearch });
  const scrollRef = useRef(0);
  const listboxRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollRef.current;
    }
  }, [externalTransferList]);

  // Maintaining legacy rule as being strict on E.164 will not work in all cases as we can dial store extensions in MyCar.
  const isValidPhoneNumber = (value: string): boolean => value.length > 3;
  const isValid = dialler.manualOutboundTimezone !== null && isValidPhoneNumber(dialler.manualOutboundNumber);

  const handleCall = () => {
    if (!isValid || !socketRef.current) return;

    socketRef.current.placeManualOutboundCall(dialler.manualOutboundNumber, dialler.manualOutboundTimezone!.value);
    dispatch({ type: 'RESET_MANUAL_OUTBOUND' });
  };

  const handleCancel = () => {
    dispatch({ type: 'RESET_MANUAL_OUTBOUND' });
  };

  return (
    <Card sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
      <CardContent>
        <Typography variant='h6' fontWeight={600} mb={2}>
          Manually place a call:
        </Typography>

        <Autocomplete
          size='small'
          freeSolo
          options={externalTransferList}
          filterOptions={(x) => x}
          getOptionLabel={(opt: string | ExternalTransferEntry) =>
            typeof opt === 'string' ? opt : `${opt.name} (${opt.phoneNumber})`
          }
          ListboxProps={{
            style: { maxHeight: 200 },
            onScroll: (e: React.SyntheticEvent) => {
              const listbox = e.currentTarget as HTMLUListElement;
              listboxRef.current = listbox;
              scrollRef.current = listbox.scrollTop;
              if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10) {
                getNextPage();
              }
            },
          }}
          inputValue={dialler.manualOutboundNumber}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') {
              setSearch(value);
              dispatch({ type: 'SET_MANUAL_OUTBOUND_NUMBER', payload: value });
            }
          }}
          onChange={(_, opt) => {
            if (opt && typeof opt !== 'string') dispatch({ type: 'SET_MANUAL_OUTBOUND_NUMBER', payload: opt.value });
          }}
          renderInput={(params) => <TextField {...params} label='Number' placeholder='e.g. +6123456789' />}
        />

        {/* Timezone selector (toggleManualOutbound in App.svelte) */}
        <Autocomplete
          size='small'
          sx={{ mt: 2 }}
          options={dialler.timezoneList}
          getOptionLabel={(opt: SelectOption) => opt.label}
          value={dialler.manualOutboundTimezone}
          onChange={(_, opt) => dispatch({ type: 'SET_MANUAL_OUTBOUND_TIMEZONE', payload: opt })}
          onOpen={loadTimezones}
          renderInput={(params) => <TextField {...params} label='Timezone' placeholder='Timezone...' />}
        />

        {/* Action buttons */}
        <Box mt={3} display='flex' justifyContent='center' gap={2}>
          <Button
            variant='contained'
            color='success'
            disableElevation
            disabled={!isValid}
            onClick={handleCall}
            sx={{ textTransform: 'none' }}>
            Call
          </Button>
          <Button
            variant='contained'
            color='error'
            disableElevation
            onClick={handleCancel}
            sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ManualOutboundPanel;
